import {
  Children,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { gsap } from "gsap";
import { usePrefersReducedMotion } from "@/components/home/usePrefersReducedMotion";

/**
 * ReactBits card-swap 포팅 (home-ui-spec §2.3·§8).
 * gsap 타임라인 스택 순환. 개조: 수치 고정(간격 5s/전환 0.8s/오프셋 24·−20/
 * 스케일 1→0.94→0.88/명도 1→0.72→0.5), hover·IntersectionObserver·
 * visibilitychange pause, reduced-motion 시 정적 스택 + 탭 즉시 교체.
 */
export interface CardSwapProps {
  /** 카드 폭 px — 데스크톱 300 / 모바일 240 (5:7 비율) */
  width: number;
  /** 카드 높이 px — 420 / 336 */
  height: number;
  /** 자동 스왑 간격 ms (§2.3: 5000) */
  interval?: number;
  /** 스테이지 접근성 라벨 (§9.3 carousel) */
  ariaLabel?: string;
  /** children 인덱스별 카드 라벨 — 현재(최전면) 카드 aria-label로 노출 */
  cardLabels?: string[];
  /** 최전면 카드 클릭 (children 인덱스) — 예: 해당 비서 컬렉션 이동 */
  onFrontCardClick?: (index: number) => void;
  className?: string;
  children: ReactNode;
}

const OFFSET_X = 24;
const OFFSET_Y = -20;
const SCALES = [1, 0.94, 0.88];
const BRIGHTNESS = [1, 0.72, 0.5];
const LIFT_Y = 40;

function slotProps(depth: number, total: number) {
  const clamp = Math.min(depth, SCALES.length - 1);
  return {
    x: depth * OFFSET_X,
    y: depth * OFFSET_Y,
    scale: SCALES[clamp],
    zIndex: total - depth,
    filter: `brightness(${BRIGHTNESS[clamp]})`,
  };
}

const cardStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  willChange: "transform",
};

export function CardSwap({
  width,
  height,
  interval = 5000,
  ariaLabel = "카드 로테이션",
  cardLabels,
  onFrontCardClick,
  className,
  children,
}: CardSwapProps) {
  const reduced = usePrefersReducedMotion();
  const items = Children.toArray(children);
  const total = items.length;

  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const pausedRef = useRef({ hover: false, offscreen: false, hidden: false });
  const [order, setOrder] = useState<number[]>(() =>
    Array.from({ length: total }, (_, i) => i),
  );
  const orderRef = useRef(order);

  /* 초기 스택 배치 — reduced-motion에서도 오프셋·스케일 유지(§7.4) */
  useLayoutEffect(() => {
    orderRef.current.forEach((idx, depth) => {
      const el = cardRefs.current[idx];
      if (el) gsap.set(el, { ...slotProps(depth, total), transformOrigin: "center center" });
    });
  }, [total]);

  const syncPause = useCallback(() => {
    const p = pausedRef.current;
    const paused = p.hover || p.offscreen || p.hidden;
    const tl = tlRef.current;
    if (tl) {
      if (paused) tl.pause();
      else tl.resume();
    }
  }, []);

  const swap = useCallback(
    (instant: boolean) => {
      const prev = orderRef.current;
      if (prev.length < 2 || tlRef.current) return;
      const next = [...prev.slice(1), prev[0]];
      if (next.some((idx) => !cardRefs.current[idx])) return;

      if (instant) {
        next.forEach((idx, depth) =>
          gsap.set(cardRefs.current[idx] as HTMLDivElement, slotProps(depth, total)),
        );
      } else {
        const frontEl = cardRefs.current[prev[0]] as HTMLDivElement;
        const tl = gsap.timeline({
          defaults: { ease: "power2.inOut" },
          onComplete: () => {
            tlRef.current = null;
          },
        });
        /* 최전면 부상(300ms) → 최후면 이동 + 나머지 승격(500ms) = 800ms */
        tl.to(frontEl, { y: -LIFT_Y, duration: 0.3 });
        tl.set(frontEl, { zIndex: 0 });
        tl.to(frontEl, { ...slotProps(prev.length - 1, total), duration: 0.5 });
        prev.slice(1).forEach((idx, depth) => {
          tl.to(
            cardRefs.current[idx] as HTMLDivElement,
            { ...slotProps(depth, total), duration: 0.5 },
            "<",
          );
        });
        tlRef.current = tl;
      }
      orderRef.current = next;
      setOrder(next);
    },
    [total],
  );

  /* 자동 로테이션 — reduced-motion 시 정지(§7.4) */
  useEffect(() => {
    if (reduced || total < 2) return;
    const id = window.setInterval(() => {
      const p = pausedRef.current;
      if (p.hover || p.offscreen || p.hidden) return;
      swap(false);
    }, interval);
    return () => window.clearInterval(id);
  }, [reduced, total, interval, swap]);

  /* 뷰포트 이탈·탭 비활성 시 pause — 백그라운드 rAF 낭비 금지(§2.3) */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      pausedRef.current.offscreen = !entry.isIntersecting;
      syncPause();
    });
    io.observe(el);
    const onVisibility = () => {
      pausedRef.current.hidden = document.hidden;
      syncPause();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [syncPause]);

  useEffect(
    () => () => {
      tlRef.current?.kill();
      tlRef.current = null;
    },
    [],
  );

  const setHover = (hover: boolean) => {
    pausedRef.current.hover = hover;
    syncPause();
  };

  const frontIndex = order[0] ?? 0;

  /* 키보드로 카드 순환 + 전면 카드 액션 실행 가능(§9.3·R-26) */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      swap(reduced);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onFrontCardClick?.(frontIndex);
    }
  };

  const onCardClick = (idx: number) => {
    if (idx === frontIndex) onFrontCardClick?.(idx);
    else if (reduced) swap(true); /* 탭/클릭 시 즉시 교체(§7.4) */
  };

  return (
    <div
      ref={containerRef}
      className={className}
      role="group"
      aria-roledescription="carousel"
      aria-label={
        /* 클릭 액션이 있으면 Enter로 실행 가능함을 라벨로 노출(R-26) */
        onFrontCardClick
          ? `${cardLabels?.[frontIndex] ?? ariaLabel} — press Enter to open`
          : (cardLabels?.[frontIndex] ?? ariaLabel)
      }
      tabIndex={0}
      style={{ position: "relative", width, height }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onKeyDown={onKeyDown}
    >
      {items.map((child, idx) => (
        <div
          key={idx}
          ref={(el) => {
            cardRefs.current[idx] = el;
          }}
          aria-hidden={order.indexOf(idx) !== 0}
          style={cardStyle}
          onClick={() => onCardClick(idx)}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
